import { Menu, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ROUTES } from "@/app/routes";

import RBACFlag from "@/app/components/core/RBACFlag";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useSetPetDetails } from "@/app/hooks/api_hooks/process/useSetPetDetails";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import {
  ICustomerProfileState,
  setPetDetails,
  setPetId,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { startCase } from "lodash";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router";
import { IPetSelectDropdownComponentProps } from "@components/routes/auth_routes/CustomerProfile/customer_profile_interface";
import { IoMdArrowDropdown } from "react-icons/io";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

const PetSelectDropdownComponent: React.FC<IPetSelectDropdownComponentProps> = ({
  pets,
  className,
  onlyAllowActivePetSelection,
  onPetSelect,
  variant = "ONE",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { petStatusBackgroundColors } = useMappedConstants();
  const { getRouteKey } = useRouter();
  const [params, setParams] = useSearchParams();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const petItems = useSelectMenuReducer(pets, "name", "id", {
    stateKey: "status",
  });
  const setPetData = useSetPetDetails();
  const dispatch = useDispatch();
  const { petId, customerId, petDetails } = routeState.state as ICustomerProfileState;
  const petIdFromSearchParams = params.get("pet_id");

  const getPetSelectOption = (id?: string | null): ISelectMenuItemData | null => {
    if (!id || !pets?.length) return null;
    const matchedPet = pets.find((pet) => pet?.id === id);
    if (!matchedPet) return null;
    return { id: matchedPet.id, title: matchedPet.name };
  };

  const resolveSelectedPet = (): ISelectMenuItemData => {
    const selectedFromQuery = getPetSelectOption(petIdFromSearchParams);
    if (selectedFromQuery) return selectedFromQuery;

    const selectedFromStore = getPetSelectOption(petId);
    if (selectedFromStore) return selectedFromStore;

    const selectedFromStorage = getPetSelectOption(localStorage.getItem("selectedPetId"));
    if (selectedFromStorage) return selectedFromStorage;

    return { id: pets[0]?.id, title: pets[0]?.name };
  };

  const [selected, setSelected] = useState<ISelectMenuItemData>(resolveSelectedPet);

  const handleSelectPet = (pet: ISelectMenuItemData) => {
    setSelected(pet);
    dispatch(setPetId({ petId: pet?.id }));
    localStorage.setItem("selectedPetId", pet?.id);
  };

  useAfterFirstRender(() => {
    if (selected) {
      if (pets.length > 0) {
        setPetData(selected.id);
      }
      // localStorage.setItem("selectedPetId", selected?.id);
    }
  }, [selected]);

  // useEffect(() => {
  // const filteredData = pets?.filter((item) => item?.id === petId);
  // setSelected({ id: filteredData[0]?.id, title: filteredData[0]?.name });
  // }, [pets]);

  // useEffect(() => {
  //   const pet_id = localStorage.getItem("selectedPetId");
  //   console.log(pet_id, "pet_id");
  //   if (pet_id) {
  //     const filteredData = pets?.filter((item) => item?.id === pet_id);
  //     setSelected({ id: filteredData[0]?.id, title: filteredData[0]?.name });
  //   }
  // }, [localStorage.getItem("selectedPetId")]);

  useEffect(() => {
    const nextSelectedPet = resolveSelectedPet();
    if (!nextSelectedPet?.id) return;

    if (selected?.id !== nextSelectedPet.id || selected?.title !== nextSelectedPet.title) {
      setSelected(nextSelectedPet);
    }
  }, [petId, petIdFromSearchParams, pets, selected?.id, selected?.title]);

  useEffect(() => {
    if (!petIdFromSearchParams) return;
    const selectedFromQuery = getPetSelectOption(petIdFromSearchParams);
    if (!selectedFromQuery) return;
    dispatch(setPetId({ petId: selectedFromQuery.id }));
    localStorage.setItem("selectedPetId", selectedFromQuery.id);

    const nextParams = new URLSearchParams(params);
    nextParams.delete("pet_id");
    setParams(nextParams, { replace: true });
  }, [dispatch, params, petIdFromSearchParams, pets, setParams]);

  const handlePetAdd = () => {
    let url = "";
    if (customerId && petId) {
      url = `${getRouteKey("ADD_PET", "url")}/${customerId}/${petId}`;
    } else {
      url = `${getRouteKey("ADD_PET", "url")}/${customerId}${location.search}`;
    }
    navigate(url);
  };

  //ready for release tag
  return (
    <div className={clsx("", className)}>
      <Menu as="div" className="relative w-full text-left">
        <div className="w-full">
          {variant === "ONE" && (
            <Menu.Button className="relative flex items-center gap-2 w-full ">
              <div className="text-theme-xs whitespace-nowrap">
                {selected?.title ? selected?.title : petDetails?.name}
              </div>
              <FaCaretDown className="w-3 h-3 text-black  cursor-pointer" />
            </Menu.Button>
          )}
          {variant === "TWO" && (
            <SearchableSelectMenu
              placeholder="Select Pet"
              defaultSelected={selected ? selected : { title: petDetails?.name, id: petDetails?.id }}
              showDropdownIcon
              onSelectItem={(item) => {
                if (item) {
                  setSelected(item);
                  if (onPetSelect) {
                    onPetSelect(item);
                  }
                }
              }}
              errorMessages={[]}
              fieldError={null}
              register={null}
              selectItems={petItems}
            />
          )}
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={`absolute z-20 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none
          
          ${pets?.length > 6 && "overflow-auto h-56"}`}
          >
            <div className="px-1 py-1">
              {pets?.map((pet) => {
                return (
                  <Menu.Item key={pet?.id}>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          if (
                            onlyAllowActivePetSelection &&
                            onlyAllowActivePetSelection === true &&
                            pet?.status?.toLocaleLowerCase() !== "active"
                          ) {
                            toast.error(`The pet is ${pet.status} cannot be selected.`);
                            return;
                          }
                          handleSelectPet({ id: pet?.id, title: pet?.name });
                        }}
                        className={`${active || selected?.id === pet?.id ? "bg-gray-900 text-white" : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-xs relative `}
                      >
                        <span className="ml-2 flex items-center">
                          {startCase(pet?.name)}
                        </span>
                        <span
                          className={clsx(
                            `top-1/2 right-2 absolute p-1 rounded-full -translate-y-1/2`,
                            petStatusBackgroundColors[pet?.status?.toLocaleLowerCase()],
                          )}
                        ></span>
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
              {variant === "ONE" && (
                <RBACFlag actionId="ADD_PET">
                  <Menu.Item>
                    {() => (
                      <button
                        onClick={handlePetAdd}
                        className="flex justify-center items-center border-gray-100 py-2 border-t w-full text-center"
                      >
                        <PlusIcon className="w-4 h-4 text-blue-600" />
                        <span className="ml-1 font-semibold">Add Pet </span>
                      </button>
                    )}
                  </Menu.Item>
                </RBACFlag>
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default PetSelectDropdownComponent;
